// Turn page primitives into form-field candidates.
//
// Five signals cover the overwhelming majority of flat forms:
//
//   1. box glyphs in the text layer  -> checkboxes
//   2. small empty squares (vectors) -> checkboxes
//   3. runs of "_" or "." in text    -> text fields covering the run
//   4. horizontal rules with clear   -> text fields sitting on the rule
//      writing space above them
//   5. larger empty boxes            -> single or multi-line text fields
//
// Everything is scored, then overlapping candidates are resolved by priority so
// a square that is both "a small rect" and "inside a ruled row" only yields one
// field.
//
// Precision matters more than recall here: a missed blank costs one drag, but a
// spurious field has to be found and deleted, and dense documents (multi-column
// rulebooks, forms with decorative dividers) can generate dozens of them.

import {
  boxWidth,
  boxHeight,
  boxesOverlap,
  intersectionOverUnion,
  boxToRect,
} from './geometry.js';

export const DEFAULTS = {
  // Checkbox size is expressed relative to the page's own body text, because a
  // compact sheet (8pt type) draws 5pt boxes while a large-print form draws
  // 14pt ones. Fixed point thresholds silently miss every box on one or the
  // other. The absolute bounds only guard against degenerate extremes.
  checkboxMinFactor: 0.45,
  checkboxMaxFactor: 2.6,
  checkboxMinAbsolute: 3,
  checkboxMaxAbsolute: 30,
  checkboxAspectTolerance: 0.45,
  minRuleLength: 36,
  minUnderscoreRun: 3,
  minDotRun: 6,
  defaultFieldHeight: 16,
  minFieldHeight: 9,
  maxFieldHeight: 26,
  multilineMinHeight: 34,
  boxMinWidth: 45,
  boxMinHeight: 12,
  // A ruled blank needs meaningfully more clear space above it than the gap
  // between two ordinary lines of text, or every separator rule in a dense
  // document reads as a place to write.
  ruleClearanceFactor: 1.3,
  // Text this much larger than the body is a heading, not a field caption.
  headingFactor: 1.15,
};

const PRIORITY = { glyph: 4, checkbox: 3, underscore: 2, box: 1, rule: 0 };

/**
 * Empty-box characters used as printed checkboxes. Filled/checked variants are
 * excluded on purpose — those mark an answer rather than offer one.
 */
const BOX_GLYPHS = new Set([
  '☐', // ☐ ballot box
  '□', // □ white square
  '▢', // ▢ white square with rounded corners
  '▭', // ▭ white rectangle
  '◻', // ◻ white medium square
  '◽', // ◽ white medium-small square
  '▫', // ▫ white small square
  '⬜', // ⬜ white large square
  '❏', // ❏ lower right drop-shadowed white square
  '❐', // ❐ upper right drop-shadowed white square
  '❑', // ❑ lower right shadowed white square
  '❒', // ❒ upper right shadowed white square
  '〓', // 〓 geta mark, used as a box in some CJK forms
]);

/**
 * Symbol fonts where an ordinary letter renders as an empty box. Filled and
 * checked variants are excluded so a ticked box is not offered as a blank one.
 * Font names commonly carry a subset prefix ("ABCDEF+Wingdings"), hence the
 * substring match.
 */
const SYMBOL_FONT_BOXES = [
  // 0x6F-0x72 are the shadowed/plain empty squares.
  { font: /zapfdingbats|dingbats/i, chars: new Set(['o', 'p', 'q', 'r']) },
  { font: /wingdings/i, chars: new Set(['o', 'q', '¨']) },
  { font: /webdings/i, chars: new Set(['£']) },
];

export function detectFields(primitives, options = {}) {
  const cfg = { ...DEFAULTS, ...options };
  const { pageBox, lines, rects, text } = primitives;
  const pageArea = boxWidth(pageBox) * boxHeight(pageBox);

  const bodySize = medianFontHeight(text);
  const ctx = {
    cfg,
    text,
    bodySize,
    lineHeight: bodySize * 1.2,
    columns: findColumns(text, pageBox, bodySize),
  };

  ctx.checkboxMin = Math.max(cfg.checkboxMinAbsolute, ctx.bodySize * cfg.checkboxMinFactor);
  ctx.checkboxMax = Math.min(cfg.checkboxMaxAbsolute, ctx.bodySize * cfg.checkboxMaxFactor);

  const candidates = [
    ...detectGlyphCheckboxes(text, ctx),
    ...detectSquareCheckboxes(rects, text, ctx, pageArea),
    ...detectFromCharRuns(text, cfg),
    ...detectFromRules(lines, ctx),
    ...detectFromBoxes(rects, text, cfg, pageArea),
  ];

  const kept = dedupe(candidates);
  for (const c of kept) {
    if (c.label === undefined) c.label = labelFor(c, ctx);
  }
  return assignNames(kept);
}

/* ------------------------------------------------------------------ */
/* page statistics                                                     */
/* ------------------------------------------------------------------ */

function medianFontHeight(text) {
  const heights = text.map((t) => t.fontHeight).filter((h) => h > 0).sort((a, b) => a - b);
  if (heights.length === 0) return 10;
  const mid = Math.floor(heights.length / 2);
  return heights.length % 2 ? heights[mid] : (heights[mid - 1] + heights[mid]) / 2;
}

/**
 * Find column bands by projecting text onto the x axis and looking for vertical
 * gutters. Without this, label lookup happily reaches across a gutter and names
 * a field after text belonging to the neighbouring column.
 */
function findColumns(text, pageBox, bodySize) {
  const whole = [{ x0: pageBox.x0, x1: pageBox.x1 }];
  if (boxWidth(pageBox) <= 0 || text.length < 12) return whole;

  // Columns are found from where lines *start*, not from vertical whitespace.
  // Projecting whitespace fails on any real layout, because a single element
  // spanning two columns — a pull quote, an intro paragraph, a rule — bridges
  // the gutter and merges them. Left edges stay put regardless.
  const clusters = [];
  for (const x of text.map((t) => t.box.x0).sort((a, b) => a - b)) {
    const last = clusters[clusters.length - 1];
    if (last && x - last.x <= 3) {
      last.count += 1;
      last.sum += x;
      last.x = last.sum / last.count;
    } else {
      clusters.push({ x, sum: x, count: 1 });
    }
  }

  const minLines = Math.max(4, Math.floor(text.length * 0.06));
  const peaks = clusters.filter((c) => c.count >= minLines).map((c) => c.x);
  if (peaks.length < 2) return whole;

  // Indented runs (bullet text, hanging labels) cluster just inside their
  // column and must not be mistaken for one, so peaks too close together are
  // folded into the leftmost.
  const minColumnWidth = Math.max(60, bodySize * 8);
  const starts = [];
  for (const p of peaks) {
    if (starts.length === 0 || p - starts[starts.length - 1] >= minColumnWidth) starts.push(p);
  }
  if (starts.length < 2) return whole;

  // Boundaries sit a little left of each column's text, because the markers a
  // column owns — checkboxes, bullets, numbers — are printed outside its text
  // edge and would otherwise be assigned to the column before it.
  const margin = Math.max(2, bodySize * 1.5);
  return starts.map((start, i) => ({
    x0: i === 0 ? pageBox.x0 : start - margin,
    x1: i + 1 < starts.length ? starts[i + 1] - margin : pageBox.x1,
  }));
}

function sameColumn(ctx, a, b) {
  return columnOf(ctx.columns, a) === columnOf(ctx.columns, b);
}

/** Index of the column containing this box, or the nearest one if it sits in a gutter. */
function columnOf(columns, box) {
  const center = (box.x0 + box.x1) / 2;
  let nearest = 0;
  let nearestDist = Infinity;
  for (let i = 0; i < columns.length; i++) {
    if (center >= columns[i].x0 - 2 && center <= columns[i].x1 + 2) return i;
    const dist = Math.min(Math.abs(center - columns[i].x0), Math.abs(center - columns[i].x1));
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = i;
    }
  }
  return nearest;
}

/* ------------------------------------------------------------------ */
/* signal 1: box glyphs in the text layer                              */
/* ------------------------------------------------------------------ */

function detectGlyphCheckboxes(text, ctx) {
  const out = [];
  for (const item of text) {
    const boxChars = boxCharsFor(item);
    if (!boxChars) continue;

    for (let i = 0; i < item.str.length; i++) {
      if (!boxChars.has(item.str[i])) continue;

      // Glyph advance is the only width information available, so the drawn box
      // is approximated from the advance and the font height.
      const advance = item.charWidth;
      const side = clamp(
        Math.min(advance * 0.95, item.fontHeight * 0.8),
        ctx.checkboxMin,
        ctx.checkboxMax,
      );
      const cx = item.box.x0 + (i + 0.5) * advance;
      const y0 = item.box.y0 + item.fontHeight * 0.05;

      out.push({
        type: 'checkbox',
        source: 'glyph',
        box: { x0: cx - side / 2, y0, x1: cx + side / 2, y1: y0 + side },
        // "☐ INITIATE" carries its own caption; prefer it over any lookup.
        inlineLabel: cleanLabel(item.str.slice(i + 1)),
        score: 0.95,
      });
    }
  }
  return out;
}

function boxCharsFor(item) {
  for (const ch of item.str) {
    if (BOX_GLYPHS.has(ch)) return BOX_GLYPHS;
  }
  const font = item.fontName || item.fontFamily || '';
  if (font) {
    for (const entry of SYMBOL_FONT_BOXES) {
      if (entry.font.test(font)) {
        for (const ch of item.str) if (entry.chars.has(ch)) return entry.chars;
      }
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* signal 2: small drawn squares                                       */
/* ------------------------------------------------------------------ */

function detectSquareCheckboxes(rects, text, ctx, pageArea) {
  const { cfg } = ctx;
  const out = [];
  for (const r of rects) {
    const w = boxWidth(r.box);
    const h = boxHeight(r.box);
    if (w < ctx.checkboxMin || w > ctx.checkboxMax) continue;
    if (h < ctx.checkboxMin || h > ctx.checkboxMax) continue;
    if (Math.abs(w / h - 1) > cfg.checkboxAspectTolerance) continue;
    if (w * h > pageArea * 0.02) continue;
    // A square with a glyph already in it is a printed marker, not a blank box.
    if (text.some((t) => coverage(t.box, r.box) > 0.5)) continue;
    out.push({ type: 'checkbox', source: 'checkbox', box: r.box, score: 0.9 });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* signal 3: underscore / dot-leader runs                              */
/* ------------------------------------------------------------------ */

function detectFromCharRuns(text, cfg) {
  const out = [];
  for (const item of text) {
    for (const run of findRuns(item.str, '_', cfg.minUnderscoreRun)) {
      out.push(runCandidate(item, run, cfg, 0.85));
    }
    for (const run of findRuns(item.str, '.', cfg.minDotRun)) {
      out.push(runCandidate(item, run, cfg, 0.6));
    }
  }
  return out;
}

function runCandidate(item, run, cfg, score) {
  const x0 = item.box.x0 + run.start * item.charWidth;
  const x1 = item.box.x0 + run.end * item.charWidth;
  const height = clamp(item.fontHeight * 1.35, cfg.defaultFieldHeight, cfg.maxFieldHeight);
  return {
    type: 'text',
    source: 'underscore',
    box: { x0, y0: item.box.y0 - 1, x1, y1: item.box.y0 - 1 + height },
    // "Email: ______" keeps its label in the same text item as the run, where
    // no positional search can find it. Capture it while we still have context.
    inlineLabel: cleanLabel(item.str.slice(0, run.start)),
    score,
  };
}

function findRuns(str, ch, minLength) {
  const runs = [];
  let start = -1;
  for (let i = 0; i <= str.length; i++) {
    const isChar = i < str.length && str[i] === ch;
    if (isChar && start === -1) start = i;
    if (!isChar && start !== -1) {
      if (i - start >= minLength) runs.push({ start, end: i });
      start = -1;
    }
  }
  return runs;
}

/* ------------------------------------------------------------------ */
/* signal 4: horizontal rules                                          */
/* ------------------------------------------------------------------ */

function detectFromRules(lines, ctx) {
  const { cfg, text } = ctx;
  const out = [];

  for (const line of lines) {
    if (line.orientation !== 'h') continue;
    if (line.length < cfg.minRuleLength) continue;
    if (line.thickness > 3) continue;

    const y = line.box.y0;
    const height = ruleFieldHeight(line, ctx);
    if (height === null) continue;

    const zone = { x0: line.box.x0, y0: y + 1, x1: line.box.x1, y1: y + height };

    // Distinguish a blank from an underline. Both have text above the rule, so
    // presence alone is not the signal — an underline (or a table cell border)
    // runs the *width of its text*, whereas a blank runs well past its label.
    if (coveredWidthFraction(zone, text) > 0.7) continue;

    const box = { x0: line.box.x0, y0: y + 0.5, x1: line.box.x1, y1: y + 0.5 + height };

    // A rule that cannot be named is almost always decorative: a section
    // divider, a separator between list items, or a border. Requiring a caption
    // is what keeps dense documents from sprouting dozens of phantom fields.
    const label = ruleLabel(box, ctx);
    if (label === null) continue;

    out.push({ type: 'text', source: 'rule', box, label, score: 0.7 });
  }
  return out;
}

/**
 * How tall the box above a rule should be: matched to nearby type so it looks
 * native, then clamped to the clear space above the rule. Returns null when the
 * clear space is no bigger than an ordinary line gap, which means the rule sits
 * inside a block of text rather than under writing space.
 */
function ruleFieldHeight(line, ctx) {
  const { cfg, text } = ctx;
  let sizeRef = null;
  let bestDist = Infinity;
  let ceiling = Infinity;

  for (const t of text) {
    const vGap = Math.abs(t.box.y0 - line.box.y0);
    if (vGap <= 40) {
      const dist = vGap + horizontalGap(t.box, line.box);
      if (dist < bestDist) {
        bestDist = dist;
        sizeRef = t;
      }
    }
    const overlapsX = Math.min(t.box.x1, line.box.x1) - Math.max(t.box.x0, line.box.x0);
    if (overlapsX > 1 && t.box.y0 > line.box.y0) {
      ceiling = Math.min(ceiling, t.box.y0 - line.box.y0 - 1.5);
    }
  }

  const required = Math.max(cfg.minFieldHeight, ctx.lineHeight * cfg.ruleClearanceFactor);
  if (ceiling < required) return null;

  const preferred = sizeRef
    ? clamp(sizeRef.fontHeight * 1.35, cfg.defaultFieldHeight, cfg.maxFieldHeight)
    : cfg.defaultFieldHeight;
  return Math.min(preferred, ceiling);
}

/**
 * Caption for a ruled blank, or null when none applies. Accepts a label to the
 * left on the same row, a short label directly above, or a signature-style
 * caption underneath — but never a heading, and never the opening line of a
 * paragraph, both of which indicate a decorative rule.
 */
function ruleLabel(box, ctx) {
  const { cfg, text } = ctx;
  const rowCenter = (box.y0 + box.y1) / 2;
  let best = null;
  let bestScore = Infinity;

  for (const t of text) {
    const clean = cleanLabel(t.str);
    if (!clean) continue;
    if (!sameColumn(ctx, t.box, box)) continue;

    const sameRow = Math.abs((t.box.y0 + t.box.y1) / 2 - rowCenter) <= Math.max(t.fontHeight, 8);
    let score = Infinity;

    if (sameRow && t.box.x1 <= box.x0 + 2 && box.x0 - t.box.x1 < 220) {
      score = box.x0 - t.box.x1;
    } else if (t.box.y0 > box.y1 - 2 && t.box.y0 - box.y1 < 24 && horizontalGap(t.box, box) < 60) {
      // Label stacked above the blank.
      if (isHeading(t, ctx) || !isStandaloneLine(t, ctx)) continue;
      score = (t.box.y0 - box.y1) * 2 + 30;
    } else if (t.box.y1 <= box.y0 + 2 && box.y0 - t.box.y1 < 16 && horizontalGap(t.box, box) < 40) {
      // Signature style: the rule comes first, its caption sits underneath.
      if (isHeading(t, ctx) || !isStandaloneLine(t, ctx)) continue;
      score = (box.y0 - t.box.y1) * 2 + 20;
    }

    if (score < bestScore) {
      bestScore = score;
      best = clean;
    }
  }
  return best;
}

function isHeading(item, ctx) {
  return item.fontHeight > ctx.bodySize * ctx.cfg.headingFactor;
}

/**
 * Is this text a caption in its own right, rather than one line of a wrapped
 * paragraph? A caption sits alone; a paragraph line has a neighbour one line
 * away. Without this, the tail of any sentence printed near a decorative rule
 * reads as that rule's label and turns it into a field.
 */
function isStandaloneLine(item, ctx) {
  const limit = ctx.lineHeight * 1.6;
  return !ctx.text.some((other) => {
    if (other === item) return false;
    const gap = Math.abs(other.box.y0 - item.box.y0);
    if (gap <= 1 || gap > limit) return false;
    return Math.min(other.box.x1, item.box.x1) - Math.max(other.box.x0, item.box.x0) > 1;
  });
}

/* ------------------------------------------------------------------ */
/* signal 5: empty boxes                                               */
/* ------------------------------------------------------------------ */

function detectFromBoxes(rects, text, cfg, pageArea) {
  const out = [];
  for (const r of rects) {
    const w = boxWidth(r.box);
    const h = boxHeight(r.box);
    if (w < cfg.boxMinWidth || h < cfg.boxMinHeight) continue;
    // Page borders and table shells are not fields.
    if (w * h > pageArea * 0.5) continue;
    if (r.filled) continue;
    // How full the *box* is, not how much of some text item falls inside it —
    // any contained text scores 100% on the latter, which rejected every box
    // carrying a printed prompt ("I am called ______") even when nearly empty.
    // Measured on an inset box, because captions are routinely printed across
    // the border itself ("Strength" straddling the top edge of its stat box)
    // and that is labelling, not filling.
    if (textAreaFraction(inset(r.box), text) > 0.3) continue;

    out.push({
      type: 'text',
      source: 'box',
      multiline: h >= cfg.multilineMinHeight,
      box: r.box,
      score: 0.65,
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* overlap resolution                                                  */
/* ------------------------------------------------------------------ */

function dedupe(candidates) {
  const sorted = [...candidates].sort(
    (a, b) => PRIORITY[b.source] - PRIORITY[a.source] || b.score - a.score,
  );
  const kept = [];
  for (const c of sorted) {
    const clash = kept.some(
      (k) => intersectionOverUnion(k.box, c.box) > 0.3 || contains(k.box, c.box),
    );
    if (!clash) kept.push(c);
  }
  // Reading order: top-to-bottom, then left-to-right, so tab order is sane.
  kept.sort((a, b) => b.box.y0 - a.box.y0 || a.box.x0 - b.box.x0);
  return kept;
}

function contains(outer, inner) {
  return (
    inner.x0 >= outer.x0 - 0.5 &&
    inner.x1 <= outer.x1 + 0.5 &&
    inner.y0 >= outer.y0 - 0.5 &&
    inner.y1 <= outer.y1 + 0.5
  );
}

/* ------------------------------------------------------------------ */
/* labels + names                                                      */
/* ------------------------------------------------------------------ */

function labelFor(candidate, ctx) {
  if (candidate.inlineLabel) return candidate.inlineLabel;
  if (candidate.source === 'box') {
    const own = boxLabel(candidate.box, ctx);
    if (own) return own;
  }

  const box = candidate.box;
  const rowCenter = (box.y0 + box.y1) / 2;
  let best = null;
  let bestScore = Infinity;

  for (const t of ctx.text) {
    const clean = cleanLabel(t.str);
    if (!clean) continue;
    if (!sameColumn(ctx, t.box, box)) continue;

    const tCenter = (t.box.y0 + t.box.y1) / 2;
    const sameRow = Math.abs(tCenter - rowCenter) <= Math.max(t.fontHeight, 8);

    let score = Infinity;
    if (candidate.type === 'checkbox') {
      // Checkbox captions normally sit to the right of the box.
      if (sameRow && t.box.x0 >= box.x1 - 2 && t.box.x0 - box.x1 < 90) {
        score = t.box.x0 - box.x1;
      } else if (sameRow && t.box.x1 <= box.x0 + 2 && box.x0 - t.box.x1 < 90) {
        score = box.x0 - t.box.x1 + 25;
      }
    } else if (sameRow && t.box.x1 <= box.x0 + 2 && box.x0 - t.box.x1 < 220) {
      score = box.x0 - t.box.x1;
    } else if (t.box.y0 > box.y1 - 2 && t.box.y0 - box.y1 < 24) {
      const dx = horizontalGap(t.box, box);
      if (dx < 60) score = (t.box.y0 - box.y1) * 2 + dx + 30;
    }

    if (score < bestScore) {
      bestScore = score;
      best = clean;
    }
  }
  return best || '';
}

/**
 * Caption for a bordered box. A box names itself: the caption is printed inside
 * it, straddling its border, or tucked immediately above or below — never off in
 * a neighbouring paragraph, which is what a generic proximity search finds.
 */
function boxLabel(box, ctx) {
  const near = Math.max(6, ctx.lineHeight);
  let best = null;
  let bestScore = Infinity;

  for (const t of ctx.text) {
    const clean = cleanLabel(t.str);
    if (!clean) continue;
    const cx = (t.box.x0 + t.box.x1) / 2;
    const cy = (t.box.y0 + t.box.y1) / 2;
    // Horizontally the caption belongs to this box.
    if (cx < box.x0 - 2 || cx > box.x1 + 2) continue;

    let score;
    if (cy >= box.y0 && cy <= box.y1) {
      score = 0; // printed inside
    } else if (cy > box.y1 && cy - box.y1 <= near) {
      score = cy - box.y1 + 1; // sitting on or just above the top edge
    } else if (cy < box.y0 && box.y0 - cy <= near) {
      score = box.y0 - cy + 1; // tucked under the bottom edge
    } else {
      continue;
    }

    if (score < bestScore) {
      bestScore = score;
      best = clean;
    }
  }
  return best;
}

function cleanLabel(str) {
  let cleaned = '';
  for (const ch of str) cleaned += BOX_GLYPHS.has(ch) ? ' ' : ch;
  cleaned = cleaned
    .replace(/[_.]{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[:*]+\s*$/, '')
    .trim();
  if (cleaned.length < 2) return '';
  if (!/[A-Za-z0-9]/.test(cleaned)) return '';
  return cleaned.split(' ').slice(0, 8).join(' ');
}

function assignNames(candidates) {
  const used = new Map();
  return candidates.map((c, index) => {
    const base = slug(c.label) || (c.type === 'checkbox' ? 'checkbox' : 'field');
    const n = (used.get(base) || 0) + 1;
    used.set(base, n);
    return {
      ...c,
      id: `f${index}`,
      name: n === 1 ? base : `${base}_${n}`,
      rect: boxToRect(c.box),
    };
  });
}

function slug(label) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

/* ------------------------------------------------------------------ */

function clamp(value, lo, hi) {
  return Math.max(lo, Math.min(value, hi));
}

/** Shrink a box away from its own border, where captions tend to sit. */
function inset(box) {
  const pad = Math.min(boxWidth(box), boxHeight(box)) * 0.12;
  return { x0: box.x0 + pad, y0: box.y0 + pad, x1: box.x1 - pad, y1: box.y1 - pad };
}

/** Fraction of `box`'s area covered by printed text. */
function textAreaFraction(box, text) {
  const area = boxWidth(box) * boxHeight(box);
  if (area <= 0) return 1;
  let inside = 0;
  for (const t of text) {
    const ix = Math.min(t.box.x1, box.x1) - Math.max(t.box.x0, box.x0);
    const iy = Math.min(t.box.y1, box.y1) - Math.max(t.box.y0, box.y0);
    if (ix > 0 && iy > 0) inside += ix * iy;
  }
  return inside / area;
}

function coverage(inner, outer) {
  if (!boxesOverlap(inner, outer)) return 0;
  const ix = Math.min(inner.x1, outer.x1) - Math.max(inner.x0, outer.x0);
  const iy = Math.min(inner.y1, outer.y1) - Math.max(inner.y0, outer.y0);
  const area = boxWidth(inner) * boxHeight(inner);
  return area > 0 ? (ix * iy) / area : 0;
}

/**
 * Fraction of `zone`'s width covered by text that meaningfully overlaps it
 * vertically. Overlapping spans are merged so two words do not double-count.
 */
function coveredWidthFraction(zone, text) {
  const width = boxWidth(zone);
  if (width <= 0) return 0;

  const spans = [];
  for (const t of text) {
    const vOverlap = Math.min(t.box.y1, zone.y1) - Math.max(t.box.y0, zone.y0);
    if (vOverlap <= 0) continue;
    if (vOverlap / Math.max(boxHeight(t.box), 1) < 0.3) continue;
    const x0 = Math.max(t.box.x0, zone.x0);
    const x1 = Math.min(t.box.x1, zone.x1);
    if (x1 > x0) spans.push([x0, x1]);
  }
  if (spans.length === 0) return 0;

  spans.sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let [curStart, curEnd] = spans[0];
  for (let i = 1; i < spans.length; i++) {
    const [s, e] = spans[i];
    if (s > curEnd) {
      covered += curEnd - curStart;
      [curStart, curEnd] = [s, e];
    } else {
      curEnd = Math.max(curEnd, e);
    }
  }
  covered += curEnd - curStart;
  return covered / width;
}

function horizontalGap(a, b) {
  if (a.x1 < b.x0) return b.x0 - a.x1;
  if (b.x1 < a.x0) return a.x0 - b.x1;
  return 0;
}
